<script setup lang="ts">
import Chart from 'primevue/chart';
import ChartLegendItem from './ChartLegendItem.vue';
import { ref, watch } from 'vue';
import { useAttendanceStore } from '@/store';
import { storeToRefs } from 'pinia';
import { THERAPY_RESULT_TYPES_NAME } from '@/enum/calendar';

interface LabelInterface {
  title: string;
  color: string;
  textColor: string;
  numberColor: string;
  total: string;
  percentage: string;
  order: number;
}

interface DataInterface {
  name: string;
  percentage: number;
  count: number;
}

const { loading, results, resultsTotal, studentName } = storeToRefs(useAttendanceStore());

const labels = ref<LabelInterface[]>([]);
const titles = ref<string[]>([]);
const chartDataNumbers = ref<number[]>([]);
const chartOptions = ref({
  plugins: {
    legend: {
      display: false,
    },
  },
});
const chartData = ref({});
const colors = ref<string[]>([]);
const attendanceCheck = ref(0);
const getObjectStyleByTherapyResultName = (result: DataInterface): LabelInterface | null => {
  attendanceCheck.value += result.percentage;
  switch (result.name) {
    case THERAPY_RESULT_TYPES_NAME.PRESENT:
      return {
        order: 1,
        title: result.name,
        color: '#8BCFCF',
        textColor: '#045C5C',
        numberColor: '#078888',
        total: `${result.count}`,
        percentage: `${result.percentage}`,
      };
    case THERAPY_RESULT_TYPES_NAME.NO_SCHOOL:
      return {
        order: 2,
        title: result.name,
        color: '#40B3DD',
        textColor: '#26728E',
        numberColor: '#40B3DD',
        total: `${result.count}`,
        percentage: `${result.percentage}`,
      };
    case THERAPY_RESULT_TYPES_NAME.ABSENT:
    case THERAPY_RESULT_TYPES_NAME.NO_SHOW:
      return {
        order: 3,
        title: result.name,
        color: '#EDA745',
        textColor: '#94703C',
        numberColor: '#EDA745',
        total: `${result.count}`,
        percentage: `${result.percentage}`,
      };
    case THERAPY_RESULT_TYPES_NAME.LATE_CANCEL:
      return {
        order: 4,
        title: result.name,
        color: '#7E78D2',
        textColor: '#474299',
        numberColor: '#7E78D2',
        total: `${result.count}`,
        percentage: `${result.percentage}`,
      };
    case THERAPY_RESULT_TYPES_NAME.CLINICIAN_CANCELLED:
      return {
        order: 5,
        title: result.name,
        color: '#D49BC1',
        textColor: '#7F3666',
        numberColor: '#D49BC1',
        total: result.count.toString(),
        percentage: result.percentage.toString(),
      };
    case THERAPY_RESULT_TYPES_NAME.CUSTOMER_CANCELLED:
      return {
        order: 6,
        title: result.name,
        color: '#822508',
        textColor: '#c3370b',
        numberColor: '#822508',
        total: result.count.toString(),
        percentage: result.percentage.toString(),
      };
    default:
      return null;
  }
};

const setLabels = () => {
  attendanceCheck.value = 0;

  labels.value = results.value.map(
    (result: DataInterface) => getObjectStyleByTherapyResultName(result) as LabelInterface,
  );

  labels.value = labels.value.sort((a: LabelInterface, b: LabelInterface) => a.order - b.order);
  titles.value = labels.value.map((c: LabelInterface) => String(c.title));
  chartDataNumbers.value = labels.value.map((c: LabelInterface) => Number(c.total));
  colors.value = labels.value.map((c: LabelInterface) => c.color);
  chartData.value = {
    labels: titles.value,
    datasets: [
      {
        data: chartDataNumbers.value,
        backgroundColor: colors.value,
        borderWidth: 4,
        borderColor: '#e2f4f4',
        borderRadius: 5,
        id: 'chart',
      },
    ],
  };
};

watch(
  () => results.value,
  () => setLabels(),
);
</script>

<template>
  <div v-if="!loading" :key="studentName">
    <p class="font-bold text-xl all-text-color">{{ studentName }}</p>
    <div class="m-0 p-0">
      <div v-if="attendanceCheck > 0" class="chart col-fixed m-0 p-0 pb-1">
        <Chart type="doughnut" :data="chartData" :width="100" :height="100" :options="chartOptions" />
      </div>
      <h5 v-else class="my-3 w-full font-bold">No data for {{ studentName }}</h5>
    </div>

    <div v-if="attendanceCheck > 0" class="col inline-grid">
      <ChartLegendItem
        v-for="label of labels"
        :key="label.color"
        :title="label.title"
        :total="label.total"
        :color="label.color"
        :text-color="label.textColor"
        :number-color="label.numberColor"
        :percentage="label.percentage"
      />
    </div>
    <div class="border-solid border-1 border-white"></div>

    <div v-if="attendanceCheck > 0" class="flex justify-content-between my-2 mx-4 dark-medium">
      <div class="text-xs ml-1">Total</div>
      <div class="font-bold text-sm mr-5">{{ resultsTotal }}</div>
    </div>
  </div>

  <Loader v-else :relative="true" class="flex justify-content-center align-items-center h-full" />
</template>

<style lang="scss" scoped>
.chart {
  width: 100px;
  height: 100px;
}
.legend {
  width: 200px;
  .total {
    width: 20px;
  }
}

.all-text-color {
  color: #045c5c;
}
.dark-medium {
  color: $grayDark;
}
</style>
